import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Eyebrow } from "../components/Reveal";
import { Scheduler } from "../components/Scheduler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERIENCING = [
  "Weight gain", "Difficulty losing weight", "Belly fat", "Diabetes / prediabetes", "Fatty liver",
  "High cholesterol", "High triglycerides", "High blood pressure", "Thyroid concerns", "PCOS",
  "Menopause-related concerns", "Fertility concerns", "Digestive issues", "Constipation", "Bloating",
  "Acidity / reflux", "Food intolerance", "Low energy", "Afternoon fatigue", "Brain fog", "Poor sleep",
  "Anxiety / stress", "Sugar cravings", "Joint pain", "Hair loss", "Skin concerns", "Autoimmune condition", "Other",
];

const TRIED = [
  "Dieting", "Keto / low-carb", "Intermittent fasting", "Gym", "Walking", "Functional medicine",
  "Supplements", "Weight-loss medication", "Ayurveda", "Homeopathy", "Nothing yet", "Other",
];

const STEPS = ["About you", "Your concerns", "Experiencing", "Lifestyle", "Your journey"];

const initialForm = {
  full_name: "", age: "", gender: "", mobile: "", email: "", country: "", timezone: "",
  concerns: "", goals: "", experiencing: [], sleep_hours: "", exercise: "", stress_level: "",
  tried: [], obstacles: "", six_month_goal: "", additional_notes: "", consent: false,
};

const inputCls =
  "w-full bg-white border border-line px-4 py-3.5 text-[15px] text-ink placeholder:text-inkmuted/50 focus:outline-none focus:border-forest focus:ring-1 focus:ring-forest/30 transition-colors";
const labelCls = "block font-mono text-[11px] tracking-[0.2em] uppercase text-inkmuted mb-2.5";
const qCls = "font-serif text-xl sm:text-2xl text-ink leading-snug mb-5";

const Field = ({ label, required, children, id }) => (
  <div>
    <label htmlFor={id} className={labelCls}>
      {label} {required && <span className="text-gold">*</span>}
    </label>
    {children}
  </div>
);

const ChipGroup = ({ options, values, onToggle, testIdPrefix }) => (
  <div className="flex flex-wrap gap-2.5">
    {options.map((opt) => {
      const active = values.includes(opt);
      return (
        <button
          type="button"
          key={opt}
          data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onToggle(opt)}
          aria-pressed={active}
          className={`px-4 py-2.5 text-[13px] font-medium border transition-colors duration-300 ${
            active
              ? "bg-forest text-ivory border-forest"
              : "bg-white text-inkmuted border-line hover:border-gold/60"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

export default function HealthProfile() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [booking, setBooking] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const stepValid = () => {
    if (step === 0) return form.full_name.trim() && form.age && form.mobile.trim();
    if (step === 4) return form.consent;
    return true;
  };

  const next = () => {
    if (!stepValid()) {
      toast.error("Please complete the required fields before continuing.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!form.consent) {
      toast.error("Please provide your consent before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, age: parseInt(form.age, 10), email: form.email || null };
      const res = await axios.post(`${API}/health-profile`, payload);
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-ivory grain relative" data-testid="health-profile-success">
        <header className="border-b border-line bg-ivory/90">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center">
            <Link to="/"><Logo /></Link>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <CheckCircle2 size={44} className="mx-auto text-mist" strokeWidth={1.5} />
            <h1 className="mt-8 font-serif font-medium text-forest text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="health-profile-success-message">
              Thank you for sharing your story.
            </h1>
            <p className="mt-6 text-inkmuted leading-relaxed max-w-xl mx-auto">
              Your responses will help us prepare for a more focused and personalised consultation. Our team will
              review your information and contact you regarding the next step.
            </p>
            <p className="mt-8 font-mono text-[11px] tracking-[0.22em] uppercase text-gold">
              Reference · {result.ref_id}
            </p>

            {booking ? (
              <div className="mt-12 border border-gold/40 bg-paper px-7 py-10 sm:px-12" data-testid="booking-success-message">
                <CalendarCheck size={36} className="mx-auto text-forest" strokeWidth={1.5} />
                <h2 className="mt-6 font-serif text-2xl sm:text-3xl text-forest">Your consultation is requested.</h2>
                <p className="mt-4 text-inkmuted leading-relaxed max-w-md mx-auto">
                  {new Date(`${booking.date}T00:00:00`).toLocaleDateString("en-GB", {
                    weekday: "long", day: "numeric", month: "long", year: "numeric",
                  })}{" "}
                  · {booking.slot_label} IST
                </p>
                <p className="mt-3 text-sm text-inkmuted">
                  Our team will confirm your appointment shortly. Booking reference · {booking.appt_id}
                </p>
              </div>
            ) : (
              <Scheduler
                profile={{ ref_id: result.ref_id, full_name: form.full_name, mobile: form.mobile, email: form.email }}
                onBooked={setBooking}
              />
            )}

            <div className="mt-10">
              <Link
                to="/"
                data-testid="success-back-home"
                className="inline-flex items-center rounded-full bg-forest text-ivory px-9 py-4 text-sm font-semibold tracking-wide border border-gold/40 hover:bg-forestdeep transition-colors duration-300"
              >
                Return to InSight Metabolics
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory grain relative" data-testid="health-profile-page">
      <header className="border-b border-line bg-ivory/90 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-inkmuted hidden sm:block">
            Step {step + 1} of {STEPS.length}
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14 lg:py-20" data-testid="health-profile-form">
        <Eyebrow>The InSight Health Profile</Eyebrow>
        <h1 className="mt-5 font-serif font-medium text-forest text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
          Before we meet, tell us what matters to you.
        </h1>
        <p className="mt-5 text-inkmuted leading-relaxed max-w-xl">
          This short questionnaire helps us understand your primary concerns, goals and current lifestyle before
          your consultation. Approximately 5 minutes.
        </p>

        <div className="mt-10 flex gap-1.5" aria-hidden="true">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1 flex-1 transition-colors duration-500 ${i <= step ? "bg-forest" : "bg-line"}`} />
          ))}
        </div>
        <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-gold">{STEPS[step]}</p>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10"
          >
            {step === 0 && (
              <div className="space-y-7" data-testid="health-profile-step-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Full name" required id="full_name">
                    <input id="full_name" data-testid="input-full-name" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your full name" />
                  </Field>
                  <Field label="Age" required id="age">
                    <input id="age" data-testid="input-age" type="number" min="1" max="120" className={inputCls} value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="Years" />
                  </Field>
                  <Field label="Gender" id="gender">
                    <select id="gender" data-testid="input-gender" className={inputCls} value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                      <option value="">Select</option>
                      <option>Female</option>
                      <option>Male</option>
                      <option>Prefer not to say</option>
                      <option>Other</option>
                    </select>
                  </Field>
                  <Field label="Mobile / WhatsApp" required id="mobile">
                    <input id="mobile" data-testid="input-mobile" className={inputCls} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 ..." />
                  </Field>
                  <Field label="Email" id="email">
                    <input id="email" data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
                  </Field>
                  <Field label="Country" id="country">
                    <input id="country" data-testid="input-country" className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Where you live" />
                  </Field>
                  <Field label="Time zone" id="timezone">
                    <input id="timezone" data-testid="input-timezone" className={inputCls} value={form.timezone} onChange={(e) => set("timezone", e.target.value)} placeholder="e.g. IST (GMT+5:30)" />
                  </Field>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-12" data-testid="health-profile-step-2">
                <div>
                  <p className={qCls}>What brings you to InSight Metabolics today?</p>
                  <textarea data-testid="input-concerns" rows={5} className={inputCls} value={form.concerns} onChange={(e) => set("concerns", e.target.value)} placeholder="Share, in your own words, the concerns that made you reach out." />
                </div>
                <div>
                  <p className={qCls}>What are the top 3 health goals you would like us to help you achieve?</p>
                  <textarea data-testid="input-goals" rows={5} className={inputCls} value={form.goals} onChange={(e) => set("goals", e.target.value)} placeholder="1. ... 2. ... 3. ..." />
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="health-profile-step-3">
                <p className={qCls}>Which of the following are you currently experiencing?</p>
                <ChipGroup options={EXPERIENCING} values={form.experiencing} onToggle={(v) => toggle("experiencing", v)} testIdPrefix="exp" />
              </div>
            )}

            {step === 3 && (
              <div className="space-y-12" data-testid="health-profile-step-4">
                <div>
                  <p className={qCls}>How many hours do you usually sleep?</p>
                  <select data-testid="input-sleep" className={inputCls} value={form.sleep_hours} onChange={(e) => set("sleep_hours", e.target.value)}>
                    <option value="">Select</option>
                    <option>Less than 5 hours</option>
                    <option>5–6 hours</option>
                    <option>6–7 hours</option>
                    <option>7–8 hours</option>
                    <option>More than 8 hours</option>
                  </select>
                </div>
                <div>
                  <p className={qCls}>What does your current exercise routine look like?</p>
                  <textarea data-testid="input-exercise" rows={4} className={inputCls} value={form.exercise} onChange={(e) => set("exercise", e.target.value)} placeholder="Walking, gym, yoga, sport — or nothing at the moment. All answers are welcome." />
                </div>
                <div>
                  <p className={qCls}>How would you rate your current stress level?</p>
                  <select data-testid="input-stress" className={inputCls} value={form.stress_level} onChange={(e) => set("stress_level", e.target.value)}>
                    <option value="">Select</option>
                    <option>Low</option>
                    <option>Moderate</option>
                    <option>High</option>
                    <option>Very high</option>
                  </select>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-12" data-testid="health-profile-step-5">
                <div>
                  <p className={qCls}>What have you already tried?</p>
                  <ChipGroup options={TRIED} values={form.tried} onToggle={(v) => toggle("tried", v)} testIdPrefix="tried" />
                </div>
                <div>
                  <p className={qCls}>What made it difficult to achieve lasting results?</p>
                  <textarea data-testid="input-obstacles" rows={4} className={inputCls} value={form.obstacles} onChange={(e) => set("obstacles", e.target.value)} placeholder="Time, consistency, conflicting advice, side effects — anything at all." />
                </div>
                <div className="border-l-2 border-gold pl-6 py-1">
                  <p className={qCls}>
                    If we could improve one thing about your health over the next six months, what would you want
                    it to be?
                  </p>
                  <textarea data-testid="input-six-month-goal" rows={4} className={inputCls} value={form.six_month_goal} onChange={(e) => set("six_month_goal", e.target.value)} placeholder="The one change that would matter most to you." />
                </div>
                <div>
                  <p className={qCls}>Is there anything else you would like Dr Rohan Dikshit to know before your consultation?</p>
                  <textarea data-testid="input-additional-notes" rows={4} className={inputCls} value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} placeholder="Optional." />
                </div>
                <div className="bg-paper border border-line p-6">
                  <label className="flex items-start gap-3.5 cursor-pointer" htmlFor="consent">
                    <input id="consent" data-testid="input-consent" type="checkbox" className="mt-1 h-4 w-4 shrink-0" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} />
                    <span className="text-[13px] text-inkmuted leading-relaxed">
                      I consent to InSight Metabolics storing and reviewing the information I have provided so the
                      clinical team can prepare for my consultation and contact me about the next step. I understand
                      this form is not for medical emergencies, and that submitting it does not by itself establish
                      a doctor–patient relationship.
                    </span>
                  </label>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-14 flex items-center justify-between gap-4">
          {step > 0 ? (
            <button
              type="button"
              data-testid="form-back-btn"
              onClick={() => { setStep((s) => s - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="inline-flex items-center gap-2 text-sm font-semibold text-inkmuted hover:text-forest transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
          ) : (
            <span />
          )}
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              data-testid="form-next-btn"
              onClick={next}
              className="inline-flex items-center gap-2.5 rounded-full bg-forest text-ivory px-8 py-3.5 text-sm font-semibold tracking-wide border border-gold/40 hover:bg-forestdeep transition-colors duration-300"
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              data-testid="health-profile-submit-btn"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2.5 rounded-full bg-forest text-ivory px-8 py-3.5 text-sm font-semibold tracking-wide border border-gold/40 hover:bg-forestdeep transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit My Health Profile"}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
